import { addMinutes } from 'date-fns'
import { computedFn } from 'mobx-utils'
import { useContext } from 'react'

import logger from '@/lib/logger'
import { Bookings } from '@/types/types'
import { computeDate, formatTime, formatTimeString } from '@/utils/formatDateTime'
import getDurationForPax from '@/utils/getDurationForPax'
import getMaxDurationForPaxFromOfferSet from '@/utils/getMaxDurationFromOfferSet'
import OfferHelpers from '@/utils/OfferHelpers'
import StoresContext from '@/utils/StoresContext'

const useSlotSelector = () => {
  const { appStore } = useContext(StoresContext)

  const {
    isCurrentDayOpen,
    selectedDay,
    selectedRoomName,
    wish,
    isOfferRequiredInShift,
    isShiftPassedOrClosed,
    getAllAvailableSlots,
    language,
    restaurantTimezone,
    isInUpdateFlow,
    acl
  } = appStore.state

  const shifts = (isCurrentDayOpen ? selectedDay?.shifts.filter((shift) => !isShiftPassedOrClosed(shift)) : []) ?? []
  const formatSlotName = (slotName?: string) => (slotName ? formatTimeString(slotName.slice(0, 5), language) : slotName)
  const nbPaxSelected = wish.pax

  return {
    shifts,
    hasSlotsToSelect: shifts.length > 0 && shifts.some((shift) => getAllAvailableSlots(shift).length > 0),
    getAllAvailableSlots,
    nbPaxSelected,
    selectSlot: (slot: Bookings.Slot, shift: Bookings.Shift, waitingList?: boolean) => {
      if (slot.disabled) {
        return false // TODO: do something ?
      }
      wish.slot = slot.name

      const hasAvailableOffers = appStore.state.isShiftSlotAndWishCompatibleWithOffers(
        shift,
        slot,
        waitingList ? 'waitlist' : 'booking'
      )

      const isOfferRequired = isOfferRequiredInShift(shift)

      if (isOfferRequired && !hasAvailableOffers) {
        const error = new Error('You cannot select this slot')
        logger.error(error, 'Slot selection blocked', {
          restaurantId: appStore.state.restaurantId
        })
        return false
      }

      return true
    },
    formatTime: (time: Date) => {
      if (!restaurantTimezone) {
        throw Error('calling formatTime before initialization')
      }
      return formatTime(time, language, restaurantTimezone)
    },
    formatSlotName,
    formatSlot: (slot: Bookings.Slot) => {
      return formatSlotName(slot.name)
    },
    selectedRoomName,
    getSlotStatus: (slot: Bookings.Slot, shift: Bookings.Shift) => {
      const isSlotAvailable = appStore.state.isSlotAvailable(slot, shift)

      return isSlotAvailable ? 'available' : 'waitlist'
    },
    getSlotUntil: (slot: Bookings.Slot, shift: Bookings.Shift) => {
      const {
        wish: { day },
        restaurantTimezone
      } = appStore.state

      if (!restaurantTimezone) {
        throw Error('calling getSlotUntil before initialization')
      }
      const computedDate = computeDate(day, slot.name, restaurantTimezone)

      if (appStore.state.isSlotOnlyPossibleWithSomeOffer(slot, nbPaxSelected, wish.room_id)) {
        const availableOffers = appStore.state.availableOffersFromSlot(slot, shift)
        const maxOfferDuration = getMaxDurationForPaxFromOfferSet(
          availableOffers.map((offer) => appStore.state.getOfferById(offer.id)),
          nbPaxSelected
        )
        return addMinutes(computedDate, maxOfferDuration)
      }
      const bookingDuration = getDurationForPax(nbPaxSelected, slot.capacity?.turn_times ?? shift.capacity?.turn_times)
      return addMinutes(computedDate, bookingDuration)
    },
    getSelectedHighlightedOfferUntil: computedFn((slot: Bookings.Slot) => {
      const {
        selectedHighlightedOffer: offer,
        wish: { pax, day },
        restaurantTimezone
      } = appStore.state
      if (!offer || !offer.has_duration) {
        return null
      }
      if (!restaurantTimezone) {
        throw Error('calling getSelectedHighlightedOfferUntil before initialization')
      }

      const offerDuration = getDurationForPax(pax, offer.turn_times)
      const computedDate = computeDate(day, slot.name, restaurantTimezone)
      return addMinutes(computedDate, offerDuration)
    }),
    shouldDisplaySuggestedRestaurantsForShift: (shift: Bookings.Shift) => {
      return (
        !isInUpdateFlow &&
        !getAllAvailableSlots(shift).filter(
          (slot) =>
            appStore.state.isSlotAvailable(slot, shift) || appStore.state.isSlotAvailableForWaitlist(slot, shift)
        ).length &&
        appStore.state.isCurrentDayAvailableInSuggestedRestaurantsBetween(shift.open, shift.close)
      )
    },
    hasDisplayEndTimeAcl: acl.includes('display_reservation_end_time'),
    formattedSlotNameSelected: wish.slot ? formatSlotName(wish.slot) : undefined,
    isSlotOnlyPossibleWithSqueezedOffer: computedFn((slot: Bookings.Slot) => {
      const isSlotPossibleWithoutOffer = !appStore.state.isSlotOnlyPossibleWithSomeOffer(
        slot,
        nbPaxSelected,
        wish.room_id
      )
      if (isSlotPossibleWithoutOffer) {
        return false
      }
      const possibleOffers = slot.offers?.filter((offer) => OfferHelpers.isPossibleForPax(offer, nbPaxSelected))
      return possibleOffers?.every((offer) => appStore.state.getOfferById(offer.id)?.is_squeezed)
    })
  }
}

export default useSlotSelector
